/**
 * ApiService - Frontend API client
 * Handles all communication with the backend
 * Supports both local mode (Express backend) and cloud mode (Netlify Functions)
 */

import type { Board, Sprint, Issue, SquadMember } from '../types';

const API_BASE = '/api';
const STORAGE_KEY = 'easydaily_config';

/**
 * Jira configuration stored in localStorage
 */
interface JiraConfig {
  jiraHost: string;
  jiraEmail: string;
  jiraToken: string;
  storyPointsField: string;
  storyPointsFieldName: string;
  configuredAt: string;
}

/**
 * Jira field definition
 */
interface JiraField {
  id: string;
  name: string;
  schema?: {
    type: string;
    custom?: string;
  };
}

/**
 * Get stored Jira configuration from localStorage
 */
function getStoredConfig(): JiraConfig | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
}

/**
 * Check if we're in cloud mode (Netlify)
 * Cloud mode is when we have config in localStorage
 */
function isCloudMode(): boolean {
  return getStoredConfig() !== null;
}

/**
 * Get headers for API requests
 * In cloud mode, includes Jira credentials
 */
function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const config = getStoredConfig();
  if (config) {
    headers['x-jira-host'] = config.jiraHost;
    headers['x-jira-email'] = config.jiraEmail;
    headers['x-jira-token'] = config.jiraToken;
    if (config.storyPointsField) {
      headers['x-jira-story-points-field'] = config.storyPointsField;
    }
  }

  return headers;
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * API Service methods
 */
export const ApiService = {
  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return fetchJson('/health');
  },

  /**
   * Get all scrum boards
   */
  async getBoards(): Promise<Board[]> {
    return fetchJson('/boards');
  },

  /**
   * Get sprints for a board
   */
  async getSprints(boardId: number): Promise<Sprint[]> {
    return fetchJson(`/boards/${boardId}/sprints`);
  },

  /**
   * Get all issues in a sprint
   */
  async getSprintIssues(sprintId: number): Promise<Issue[]> {
    return fetchJson(`/sprints/${sprintId}/issues`);
  },

  /**
   * Get squad members with their issues
   */
  async getSquadMembers(sprintId: number): Promise<SquadMember[]> {
    return fetchJson(`/sprints/${sprintId}/members`);
  },

  /**
   * Get orphan issues (In Progress without assignee)
   */
  async getOrphanIssues(sprintId: number): Promise<Issue[]> {
    return fetchJson(`/sprints/${sprintId}/orphans`);
  },

  /**
   * Get unestimated issues (no story points)
   */
  async getUnestimatedIssues(sprintId: number): Promise<Issue[]> {
    return fetchJson(`/sprints/${sprintId}/unestimated`);
  },

  /**
   * Get Jira fields (for Story Points detection)
   */
  async getFields(): Promise<JiraField[]> {
    return fetchJson('/fields');
  },

  /**
   * Assign an issue to a member
   */
  async assignIssue(issueKey: string, accountId: string): Promise<void> {
    await fetchJson(`/issues/${issueKey}/assign`, {
      method: 'PUT',
      body: JSON.stringify({ accountId }),
    });
  },

  /**
   * Create a chouquette debt issue
   */
  async createChouquetteDebt(
    projectKey: string,
    sprintId: number,
    memberName: string,
    debtCount: number
  ): Promise<{ key: string }> {
    return fetchJson('/issues/chouquette', {
      method: 'POST',
      body: JSON.stringify({ projectKey, sprintId, memberName, debtCount }),
    });
  },

  /**
   * Check if running in cloud mode (has localStorage config)
   */
  isCloudMode,

  /**
   * Get stored configuration
   */
  getConfig: getStoredConfig,
};

export default ApiService;
